type MonthPart =
  | "01"
  | "02"
  | "03"
  | "04"
  | "05"
  | "06"
  | "07"
  | "08"
  | "09"
  | "10"
  | "11"
  | "12";

type YearPart = number;

export type MonthKey = `${YearPart}-${MonthPart}`;

export const isMonthKey = (key: unknown): key is MonthKey => {
  return typeof key === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(key);
};

export enum OrgSubscriptionStatus {
  none = 0,
  xeroAppStore = 1,
  manual = 2,
  stripe = 3,
  linkedToPartner = 4,
  partnerTrialling = 5,
  stripeLifetimeIndividual = 6,
  trialingNoPaymentSetup = 7,
}

export enum SummaryDataType {
  ProfitLoss = 1,
  ProviderBudget = 2,
  Cashflow = 3,
  BalanceSheet = 4,
  HelloCashflowBudget = 5,
  AggregatedData = 6,
}

/**
 * Shared organisation option values persisted across web, admin, and API flows.
 *
 * @remarks
 * These enum numbers are part of the cross-surface data contract. Keep new
 * values append-only and verify legacy import paths with the web/admin bridge
 * tests plus
 * `services/api/src/__tests__/unit/HelloCashflowDomain/DomainTypes`.
 */
export enum OrgNotificationOptions {
  none = 0,
  emailMonthlyUpdate = 1,
}

export enum OrganisationTypes {
  transactional = 0,
  subscription = 1,
}

export enum BudgetSources {
  xero = 0,
  hellocashflow = 1,
}

export enum AccountingProvider {
  xero = 1,
  quickbooks = 2,
  sage = 3,
}

export type UserOrgRole = "admin" | "team_member";

export const USER_ORG_ROLES: readonly UserOrgRole[] = ["admin", "team_member"];

export function isUserOrgRole(value: string): value is UserOrgRole {
  return (USER_ORG_ROLES as readonly string[]).includes(value);
}

/**
 * Validates one role selected for an organisation membership grant.
 *
 * @remarks
 * Each grant has one role. An effective membership can contain both roles
 * when independently granted authorities are combined.
 */
export function assertValidUserOrgRoles(
  roles: readonly string[],
): asserts roles is readonly UserOrgRole[] {
  if (roles.length !== 1) {
    throw new Error(
      `A membership grant must contain exactly one role (received ${roles.length})`,
    );
  }
  const role = roles[0];
  if (role === undefined || !isUserOrgRole(role)) {
    throw new Error(
      `Unrecognised User_Organisation role "${role}" (allowed: ${USER_ORG_ROLES.join(", ")})`,
    );
  }
}
